import React, { useState } from 'react';
import { X, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

/* Safari share icon — the actual iOS icon so users recognise it immediately */
const ShareIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

/* Checkmark icon for step 3 */
const CheckIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* "Add to Home Screen" icon */
const AddBoxIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const IOSGuide: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: '0 16px env(safe-area-inset-bottom)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          padding: '28px 24px 36px',
          maxWidth: 380,
          width: '100%',
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
          }
        `}</style>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/logo192.png" alt="Nice" style={{ width: 44, height: 44, borderRadius: 10 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>{t('installPrompt.title')}</div>
              <div style={{ fontSize: 13, color: '#888' }}>{t('installPrompt.subtitle')}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: '#f0f0f0', border: 'none', borderRadius: '50%', width: 32, height: 32, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            aria-label={t('installPrompt.close')}
          >
            <X size={16} color="#666" />
          </button>
        </div>

        {!isSafari && (
          <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 12, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#795548' }}>
            {t('installPrompt.ios_safari_warning')}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Step number={1} icon={<ShareIcon size={22} />} color="#007AFF">
            {t('installPrompt.ios_step1')}
          </Step>
          <Step number={2} icon={<AddBoxIcon size={22} />} color="#34C759">
            {t('installPrompt.ios_step2')}
          </Step>
          <Step number={3} icon={<CheckIcon size={22} />} color="#FF9500">
            {t('installPrompt.ios_step3')}
          </Step>
        </div>
      </div>
    </div>
  );
};

const Step: React.FC<{ number: number; icon: React.ReactNode; color: string; children: React.ReactNode }> = ({ number, icon, color, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{
      width: 44, height: 44, borderRadius: 12,
      background: `${color}15`, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, position: 'relative',
    }}>
      {icon}
      <span style={{
        position: 'absolute', top: -4, left: -4,
        width: 18, height: 18, borderRadius: '50%',
        background: color, color: '#fff',
        fontSize: 10, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {number}
      </span>
    </div>
    <div style={{ fontSize: 14, color: '#333', lineHeight: 1.4 }}>
      {children}
    </div>
  </div>
);


const InstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const { canInstall, isIOS, promptInstall, dismiss } = useInstallPrompt();
  const [showGuide, setShowGuide] = useState(false);

  if (!canInstall) return null;

  const handleInstall = async () => {
    if (isIOS) {
      setShowGuide(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'accepted' || outcome === 'dismissed') {
      dismiss();
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#ffffff',
          borderTop: '1px solid #e8e8e8',
          padding: '14px 16px',
          paddingBottom: 'max(14px, calc(14px + env(safe-area-inset-bottom)))',
          paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left)))',
          paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right)))',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          zIndex: 2000,
          animation: 'installSlideUp 0.3s ease-out',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
        }}>
        <style>{`
          @keyframes installSlideUp {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
          }
        `}</style>

        <img
          src="/logo192.png"
          alt="Nice"
          style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
            {t('installPrompt.title')}
          </div>
          <div style={{ fontSize: 12, color: '#777', lineHeight: 1.3 }}>
            {t('installPrompt.description')}
          </div>
        </div>

        <button
          onClick={handleInstall}
          style={{
            background: '#0b3b2e',
            color: '#fff',
            border: 'none',
            borderRadius: 20,
            padding: '9px 18px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flexShrink: 0,
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
          }}>
          <Download size={13} />
          {t('installPrompt.install')}
        </button>

        <button
          onClick={dismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#aaa',
            flexShrink: 0,
          }}
          aria-label={t('installPrompt.dismiss')}>
          <X size={16} />
        </button>
      </div>

      {showGuide && <IOSGuide onClose={() => setShowGuide(false)} />}
    </>
  );
};

export default InstallPrompt;
